import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAuthUser, getTutorProfile } from '../services/auth';
import { ClientPrincipal, Tutor, UserRole } from '../services/types';

interface AuthContextType {
  user: ClientPrincipal | null;
  tutor: Tutor | null;
  loading: boolean;
  refetchTutor: () => Promise<void>;
  hasRole: (requiredRole: UserRole) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<ClientPrincipal | null>(null);
  const [tutor, setTutor] = useState<Tutor | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAuthData = async () => {
    setLoading(true);
    try {
      const authUser = await getAuthUser();
      setUser(authUser);

      if (authUser) {
        const tutorProfile = await getTutorProfile();
        setTutor(tutorProfile);
      }
    } catch (error) {
      console.error('Failed to fetch auth data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuthData();
  }, []);

  const refetchTutor = async () => {
    const tutorProfile = await getTutorProfile();
    setTutor(tutorProfile);
  };

  const hasRole = (requiredRole: UserRole): boolean => {
    if (!tutor) return false;

    const roleHierarchy: Record<UserRole, number> = {
      viewer: 1,
      operator: 2,
      admin: 3,
    };

    return roleHierarchy[tutor.role] >= roleHierarchy[requiredRole];
  };

  return (
    <AuthContext.Provider value={{ user, tutor, loading, refetchTutor, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
}
